/**
 * Insights: turns the stored synthesis into what the Insights tab shows.
 * Pure functions, no React, so they are easy to test and to change.
 *
 * Stored synthesis fields (functions/insights/synthesis.js):
 *   priority_fixes[]  { issue, frequency, avg_severity (critical|major|minor), recommendation, sessions[] }
 *   contradictions[]  { topic, viewpoints[] { session, stance, quote } }
 *   recurring_themes[] { theme, frequency, sessions[], supporting_quotes[] }
 *   executive_summary
 * plus, on the doc, sessionIds[] (the session each 0-based index means) and
 * sessionCount. Syntheses stored before phase 5a have no sessionIds, and very
 * old ones used `priority` and `description` on a fix; both are still read.
 */

// THE severity mapping (Duane, 25 Oct 2026: Critical, Major, Minor).
// New syntheses store critical/major/minor; older ones stored high/medium/low
// (and very old ones `priority`), read here as Critical/Major/Minor. Change
// labels, glyphs or colours here and nowhere else.
export const SEVERITY = {
  critical: { key: 'critical', rank: 0, label: 'Critical', glyph: 'C', sevClass: 'high', tagClass: 'aero-tag-red' },
  major: { key: 'major', rank: 1, label: 'Major', glyph: 'Ma', sevClass: 'med', tagClass: 'aero-tag-amber' },
  minor: { key: 'minor', rank: 2, label: 'Minor', glyph: 'Mi', sevClass: 'low', tagClass: 'aero-tag-blue' },
};

const SEVERITY_ALIASES = {
  critical: 'critical',
  high: 'critical',
  severe: 'critical',
  blocker: 'critical',
  major: 'major',
  medium: 'major',
  moderate: 'major',
  med: 'major',
  minor: 'minor',
  low: 'minor',
};

export function severityOf(value) {
  const key = typeof value === 'string' ? SEVERITY_ALIASES[value.trim().toLowerCase()] : null;
  return SEVERITY[key || 'minor'];
}

/** A value as display text; objects and arrays become '' rather than crash. */
export function asText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function asCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
}

function asIndexes(value) {
  return Array.isArray(value)
    ? value.map(Number).filter((n) => Number.isInteger(n) && n >= 0)
    : [];
}

/** One fix in display form, whatever field names it was stored with. */
export function normaliseFix(fix) {
  if (!fix || typeof fix !== 'object') return null;
  const issue = asText(fix.issue);
  if (!issue) return null;
  return {
    issue,
    severity: severityOf(fix.avg_severity ?? fix.severity ?? fix.priority),
    frequency: asCount(fix.frequency),
    description: asText(fix.description),
    recommendation: asText(fix.recommendation),
    sessions: asIndexes(fix.sessions),
  };
}

/** Fixes for "What to fix first": most severe first, then most sessions. */
export function rankFixes(fixes) {
  return (Array.isArray(fixes) ? fixes : [])
    .map(normaliseFix)
    .filter(Boolean)
    .map((fix, i) => ({ fix, i }))
    .sort((a, b) =>
      (a.fix.severity.rank - b.fix.severity.rank) ||
      (b.fix.frequency - a.fix.frequency) ||
      (a.i - b.i))
    .map(({ fix }) => fix);
}

export function normaliseTheme(theme) {
  if (typeof theme === 'string') return theme ? { theme, frequency: null, sessions: [], quotes: [] } : null;
  if (!theme || typeof theme !== 'object') return null;
  const label = asText(theme.theme);
  if (!label) return null;
  return {
    theme: label,
    frequency: theme.frequency == null ? null : asCount(theme.frequency),
    sessions: asIndexes(theme.sessions),
    quotes: (Array.isArray(theme.supporting_quotes) ? theme.supporting_quotes : []).map(asText).filter(Boolean),
  };
}

export function normaliseContradiction(item) {
  if (typeof item === 'string') return item ? { topic: item, viewpoints: [] } : null;
  if (!item || typeof item !== 'object') return null;
  const topic = asText(item.topic);
  const viewpoints = (Array.isArray(item.viewpoints) ? item.viewpoints : [])
    .filter((v) => v && typeof v === 'object')
    .map((v) => {
      const n = Number(v.session);
      return {
        session: Number.isInteger(n) && n >= 0 ? n : null,
        stance: asText(v.stance),
        quote: asText(v.quote),
      };
    })
    .filter((v) => v.stance || v.quote);
  if (!topic && viewpoints.length === 0) return null;
  return { topic, viewpoints };
}

function millis(value) {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value === 'number') return value;
  const t = Date.parse(value);
  return Number.isNaN(t) ? 0 : t;
}

/** The owner's own "Preview as tester" run: no number, not in Insights. */
export function isOwnerPreview(session) {
  return !!session && session.isOwnerPreview === true;
}

/**
 * The name for a session, the same everywhere: the tester's name if set,
 * "You" for an owner preview, otherwise "Anonymous tester". "Session #N"
 * goes alongside as the secondary label, never instead of the name.
 */
export function sessionDisplayName(session) {
  const name = asText(session?.testerName).trim();
  if (name) return name;
  return isOwnerPreview(session) ? 'You' : 'Anonymous tester';
}

/**
 * Session numbers by id. A stored `sessionNumber` (set once at creation by
 * the server) wins; older sessions are numbered by creation order, oldest 1,
 * which is where the server's counter continues from.
 */
export function getSessionNumbers(sessions) {
  // Owner previews get no number and don't take one from testers.
  const list = Array.isArray(sessions) ? sessions.filter((s) => s && s.id && !isOwnerPreview(s)) : [];
  const byCreation = [...list].sort((a, b) =>
    (millis(a.startedAt) - millis(b.startedAt)) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const numbers = new Map();
  byCreation.forEach((s, i) => {
    numbers.set(s.id, Number.isInteger(s.sessionNumber) ? s.sessionNumber : i + 1);
  });
  return numbers;
}

/**
 * The name for the session a synthesis calls `index`. New syntheses carry
 * sessionIds, so the index becomes the real session's number. Older ones
 * only have positions in an unordered list, so they get a neutral label.
 */
export function sessionLabel(index, synthesisDoc, numbers, sessions) {
  if (index == null) return null;
  const id = Array.isArray(synthesisDoc?.sessionIds) ? synthesisDoc.sessionIds[index] : null;
  const n = id && numbers ? numbers.get(id) : null;
  if (!id) return { name: `Tester ${index + 1}`, number: null };
  const session = Array.isArray(sessions) ? sessions.find((x) => x && x.id === id) : null;
  return { name: sessionDisplayName(session), number: n || null };
}

/**
 * "Based on N of M sessions", and how many analysed sessions are not in the
 * synthesis yet (they join on the next server run).
 *   n       sessions the synthesis was built from
 *   m       sessions in the test that have finished recording
 *   joining analysed sessions not in it yet
 */
export function coverage(synthesisDoc, sessions) {
  // Owner previews never count, on either side of "N of M".
  const list = Array.isArray(sessions) ? sessions.filter((x) => !isOwnerPreview(x)) : [];
  const ids = Array.isArray(synthesisDoc?.sessionIds) ? synthesisDoc.sessionIds : null;
  const n = ids ? ids.length : asCount(synthesisDoc?.sessionCount);
  const finished = list.filter((s) => s && s.status !== 'in_progress').length;
  const analysed = list.filter((s) => s && s.analysisStatus === 'completed');
  const joining = ids
    ? analysed.filter((s) => !ids.includes(s.id)).length
    : Math.max(0, analysed.length - n);
  return { n, m: Math.max(finished, n), joining, analysed: analysed.length };
}

/** The run state the server keeps in testingConfig/insightsRun. */
export function isRunBusy(run) {
  return run?.state === 'pending' || run?.state === 'running';
}
